import { useTranslate } from '@repo/shared/hooks'
import { useReferralCodeSchema } from '@repo/shared/schemas'
import { globalConfig } from '@repo/shared/utils'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Input,
  Row,
  Typography,
} from '@repo/ui/components'
import { useAppForm } from '@repo/ui/hooks'
import * as React from 'react'
import { PaymentType } from '..'
import type {
  OrderBoxReferralProps,
  ReferralData,
} from './order-box.referral.types'
import { twMerge } from 'tailwind-merge'
import type { UserOTCReferral } from '@repo/services'

export const OrderBoxReferral = (props: OrderBoxReferralProps) => {
  const {
    onReferralChange,
    flatOrderData,
    assetTitle,
    hasDefaultReferral,
    onCheckReferralCode,
    fromAmount = 0,
    referal,
  } = props
  const { t } = useTranslate()
  const [isCheckingCode, setIsCheckingCode] = React.useState(false)
  const [innerReferal, setInnerReferal] = React.useState<
    UserOTCReferral | null | undefined
  >(referal)

  React.useEffect(() => {
    if (referal) {
      setInnerReferal(referal)
    }
  }, [referal])

  // State management
  const [showReferralSection, setShowReferralSection] = React.useState(false)
  const [referralData, setReferralData] = React.useState<ReferralData | null>(
    hasDefaultReferral ? { code: '' } : null,
  )

  const referralDetails = React.useMemo(
    () => ({
      name: flatOrderData?.referralName,
      avatar: globalConfig.baseImageUrl + flatOrderData?.referralAvatar,
      commissionReturn: flatOrderData?.agentDiscount,
      commissionReturnPercentage: flatOrderData?.agentDiscountPercentage,
    }),
    [flatOrderData],
  )

  // Form setup
  const { schema: referralSchema } = useReferralCodeSchema()
  const { form: referralForm } = useAppForm({
    formSchema: referralSchema,
  })

  // Handlers
  const handleReferralToggle = () => {
    setShowReferralSection(!showReferralSection)
    if (showReferralSection) {
      // When closing the section, just reset the form
      // Don't call onReferralChange to avoid unnecessary API calls
      referralForm.reset()
      // Only clear referralData if it was set (not if it was already null)
      if (referralData) {
        setReferralData(null)
      }
    }
  }

  const handleReferralCodeChange = (value: string) => {
    if (referralForm.formState.errors.referralCode) {
      referralForm.clearErrors('referralCode')
    }
  }

  const handleInputFocus = () => {
    referralForm.clearErrors('referralCode')
  }

  const handleRemoveReferral = () => {
    referralForm.reset()
    // First, clear referralData to allow form to show
    setReferralData(null)
    // Then hide and show section to reset state
    setShowReferralSection(false)
    // Call onReferralChange to notify parent and refresh price
    // onReferralChange?.(null, () => {
    //   // Show referral section again so user can add another referral
    //   setShowReferralSection(true)
    // })
    setInnerReferal(null)
  }

  const handleCheckCode = async (data: { referralCode: string }) => {
    if (data.referralCode === 'INVALID' || data.referralCode === '') {
      referralForm.setError('referralCode', {
        message: t('order_referral_code_invalid'),
        type: 'manual',
      })
      onReferralChange?.(null, () => setReferralData(null))
      return
    }

    // If onCheckReferralCode is provided, use it to call API
    if (onCheckReferralCode) {
      console.log('Calling onCheckReferralCode with:', {
        referralCode: data.referralCode,
        fromAmount,
      })
      setIsCheckingCode(true)
      onCheckReferralCode(
        data.referralCode,
        fromAmount,
        (newReferralData) => {
          // Success: update state and show referral info
          console.log('Referral code check success:', newReferralData)
          referralForm.clearErrors('referralCode')
          onReferralChange?.(newReferralData, () =>
            setReferralData(newReferralData),
          )
          setIsCheckingCode(false)
        },
        (error) => {
          // Error: show error message
          console.error('Referral code check error:', error)
          referralForm.setError('referralCode', {
            message: t('order_referral_code_invalid'),
            type: 'manual',
          })
          onReferralChange?.(null, () => setReferralData(null))
          setIsCheckingCode(false)
        },
      )
    } else {
      console.warn(
        'onCheckReferralCode is not provided, using fallback validation',
      )
      // Fallback to simple validation
      referralForm.clearErrors('referralCode')

      const newReferralData = {
        code: data.referralCode,
      }

      onReferralChange?.(newReferralData, () =>
        setReferralData(newReferralData),
      )
    }
  }

  // Hide component for MALEK token or when user has a default referral code
  if (assetTitle === 'MALEK') return null

  return (
    <>
      {/* Referral Toggle Button */}
      {!innerReferal && (
        <Row className='justify-between'>
          <Button
            variant='transparent'
            size='sm'
            onClick={handleReferralToggle}
            className='!gap-sm !px-0'
            startIcon={
              <Icon
                name={showReferralSection ? 'XmarkLine' : 'PlusLine'}
                size='sm'
              />
            }
          >
            {t('order_referral_consultant_question')}
          </Button>
        </Row>
      )}

      {/* Referral Form */}
      {showReferralSection && !innerReferal?.userID && (
        <Col className='gap-md'>
          <Typography size='xs' color='text-neutral-surface-60'>
            {t('order_referral_consultant_description')}
          </Typography>

          <Form {...referralForm}>
            <form onSubmit={referralForm.handleSubmit(handleCheckCode)}>
              <FormField
                control={referralForm.control}
                name='referralCode'
                render={({ field }) => (
                  <FormItemWrapper inputSize='md'>
                    <Input
                      {...field}
                      placeholder={t('order_referral_consultant_placeholder')}
                      className='flex-1'
                      onChange={(e) => {
                        field.onChange(e)
                        handleReferralCodeChange(e.target.value)
                      }}
                      onBlur={handleInputFocus}
                      endIcon={
                        <Button
                          type='submit'
                          variant='fill'
                          color='base'
                          size='sm'
                          disabled={isCheckingCode}
                          loading={isCheckingCode}
                          className='min-w-20'
                        >
                          {t('order_referral_check_code')}
                        </Button>
                      }
                    />
                  </FormItemWrapper>
                )}
              />
            </form>
          </Form>
        </Col>
      )}

      {/* Successful Referral Display */}
      {innerReferal && (
        <Col
          className={twMerge(
            'gap-sm',
            innerReferal.dismissable ? 'py-0' : 'py-md',
          )}
        >
          {innerReferal.dismissable && (
            <Row className='items-center justify-between gap-md'>
              <Row className='items-center gap-sm'>
                <Avatar className='h-10 w-10'>
                  <AvatarImage
                    src={
                      referralDetails.avatar
                        ? `${referralDetails.avatar}`
                        : '/images/placeholder.webp'
                    }
                  />
                  <AvatarFallback>
                    {referralDetails.name
                      ?.slice(0, 1)
                      .toUpperCase()
                      .slice(0, 2) || 'C'}
                  </AvatarFallback>
                </Avatar>
                <Col className='gap-2xs'>
                  <Typography size='xs' color='text-neutral-surface-60'>
                    {t('order_referral_consultant_title')}
                  </Typography>
                  <Typography size='xs' weight='bold'>
                    {referralDetails.name}
                  </Typography>
                </Col>
              </Row>
              <button className='cursor-pointer' onClick={handleRemoveReferral}>
                <Icon
                  name='TrashBinLine'
                  size='sm'
                  color='fill-negative-surface-100'
                />
              </button>
            </Row>
          )}

          <Row className='justify-between py-xs'>
            <Typography size='sm' color='text-positive-surface-100'>
              {t('order_referral_commission_return', {
                percentage: referralDetails.commissionReturnPercentage ?? 0,
              })}
            </Typography>

            <PaymentType
              value={referralDetails.commissionReturn}
              color='text-positive-surface-80'
              numberColor='text-positive-surface-100'
              numberSize='md'
              size='xs'
              isBold={false}
            />
          </Row>
        </Col>
      )}
    </>
  )
}
